import { exactInteger } from '@/lib/exact'
import type { Block, Cell, ChipCell, Column, FormBlock, TableBlock } from '@/kova/spec/types'

import type { ApiClient } from '@/lib/api'
import { ApiError } from '@/lib/api/errors'

import type { Caller } from './handles'
import type { ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'
import { callerBeside, submitWrite, writeRefusal } from './writes'

/**
 * Content & Localization › Authoritative-language flags (`ct-authoritative`),
 * read from `GET /v1/admin/copy/authority` (go-wallet#157, FR-20.6/20.7,
 * handler `internal/server/admin_copy.go`).
 *
 * # Two lists, and showing either one alone is a different lie
 *
 * The endpoint is explicit about why it returns both:
 *
 *   > A console that showed only the determinations would render an empty table
 *   > on a deployment where counsel has never been asked, which reads as
 *   > "nothing to do". A console that showed only the gap … lets a recorded
 *   > answer become invisible the moment it is recorded, since the country
 *   > leaves the worklist and nothing else returns it.
 *
 * So this screen renders both, in one table, with the determination source on
 * every row — and states the counts separately rather than making an operator
 * count rows to find out how much of the worklist is left.
 *
 * # `default` is reported, never silent
 *
 * "'EN governs here' and 'nobody has asked about here' are different facts."
 * A country carrying FR-20.7's default is not a country somebody decided about;
 * it is one nobody has asked counsel about, and the row says which it is.
 *
 * # `requires_local_language` changes what a stale translation IS
 *
 * When counsel has determined that a country legally requires a local-language
 * contractual version, "a stale translation into that language is a compliance
 * exposure and not a cosmetic gap". That is the one flag on this screen with a
 * consequence outside it, so it is drawn as such rather than as a tick.
 *
 * # Nothing consumes this yet, and the screen says so
 *
 * "NO CUSTOMER DOCUMENT CONSULTS `determined` YET. It is recorded, versioned
 * and readable here; wiring it onto the disclosure renderer is the
 * `consent_texts` family's own change." A console that let this read as
 * governing what customers actually receive would be claiming an enforcement
 * that does not exist.
 */

export const COPY_AUTHORITY_PATH = '/copy/authority'

const SOURCE = 'postgres · copy_authority (GET /v1/admin/copy/authority)'
const AUTHORITY = 'operational · counsel’s determinations as recorded'

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function whole(value: unknown): number | null {
  return exactInteger(value)
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

interface Determination {
  readonly country: string
  readonly language: string
  readonly requiresLocal: boolean | null
  readonly source: string
  readonly counselReference: string
  readonly version: number | null
  readonly determinedBy: string
}

function readDetermination(raw: unknown): Determination | null {
  if (!isObject(raw)) return null
  const country = text(raw['country'])
  if (country === '') return null
  return {
    country,
    language: text(raw['authoritative_language']),
    requiresLocal: typeof raw['requires_local_language'] === 'boolean' ? raw['requires_local_language'] : null,
    source: text(raw['determination']),
    counselReference: text(raw['counsel_reference']),
    version: whole(raw['version']),
    determinedBy: text(raw['determined_by']),
  }
}

/**
 * Where the answer came from.
 *
 * `default` is a gap wearing an answer's clothes: FR-20.7 applies, and nobody
 * asked. It is amber rather than neutral for that reason.
 */
function sourceChip(d: Determination): ChipCell {
  if (d.source === 'counsel') return ['counsel', 'g']
  if (d.source === 'default') return ['DEFAULT — nobody has asked', 'a']
  return [d.source === '' ? '— not stated' : `unknown: ${d.source}`, 'r']
}

const COLS: readonly Column[] = [
  ['Country', 'm', '92px'],
  ['Authoritative language', 'm', '170px'],
  ['Local-language version required', 'c', '210px'],
  ['Determined by', 'c', 'minmax(190px,1.3fr)'],
  ['Counsel reference', 'x', 'minmax(170px,1.2fr)'],
  ['Recorded by', 'x', '150px'],
]

function row(d: Determination): readonly Cell[] {
  return [
    d.country,
    d.language === '' ? '— not stated' : d.language,
    // The one flag here with a consequence outside this screen: it decides
    // whether a stale translation is a compliance exposure or a cosmetic gap.
    d.requiresLocal === null
      ? (['— not stated', 'r'] as ChipCell)
      : d.requiresLocal
        ? (['REQUIRED by law', 'r'] as ChipCell)
        : (['not required', 'n'] as ChipCell),
    sourceChip(d),
    d.counselReference === '' ? '— none on file' : d.counselReference,
    // Absent on a country carrying the default, where nobody recorded anything.
    d.determinedBy === '' ? '— nobody' : d.determinedBy,
  ]
}

export function composeAuthority(payload: Record<string, unknown>, readAt: Date): ScreenData {
  const determined: Determination[] = []
  const undetermined: Determination[] = []
  let unreadable = 0

  for (const [key, into] of [['determined', determined], ['undetermined', undetermined]] as const) {
    const raw = Array.isArray(payload[key]) ? (payload[key] as readonly unknown[]) : []
    for (const entry of raw) {
      const parsed = readDetermination(entry)
      if (parsed === null) unreadable += 1
      else into.push(parsed)
    }
  }

  const determinedCount = whole(payload['determined_count'])
  const undeterminedCount = whole(payload['undetermined_count'])
  const requiresLocal = determined.filter((d) => d.requiresLocal === true)

  // Both lists in one table. Either alone is a different lie: only the
  // determinations reads as "nothing to do" where counsel has never been asked,
  // and only the gap makes a recorded answer invisible the moment it is
  // recorded.
  const table: TableBlock = {
    t: 'table',
    title: 'copy_authority · determinations in force, and the open jurisdictions without one',
    src: 'ledger',
    facets: false,
    cols: COLS,
    rows: [...determined, ...undetermined].map(row),
    footer: `${String(determinedCount ?? determined.length)} determination(s) on file · ${String(undeterminedCount ?? undetermined.length)} open jurisdiction(s) carrying the default${unreadable === 0 ? '' : ` · ${String(unreadable)} row(s) could not be read`}`,
    foot2:
      'A country carrying the default is not a country somebody decided about — it is one nobody has asked counsel about, and the two are different facts. A determination stays on this screen even after its jurisdiction closes, because that is what somebody needs when it reopens.',
  }

  const blocks: Block[] = [table]

  if (undetermined.length > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Worklist',
      title: `${String(undeterminedCount ?? undetermined.length)} open jurisdiction(s) have no determination on file`,
      body: 'Each carries FR-20.7’s default — the base language is authoritative and translations are informational — and that is a DEFAULT rather than an answer. Determining which countries legally require a local-language contractual version is a question for counsel, not for this console.',
      ref: undetermined.map((d) => d.country).join(' · '),
    })
  }

  if (requiresLocal.length > 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Not cosmetic',
      title: `${String(requiresLocal.length)} country/countries legally require a local-language version`,
      body: 'In these jurisdictions a stale translation is a compliance exposure rather than a cosmetic gap. The translation backlog for the languages they name is not a tidying task, and it should not be scheduled as one.',
      ref: requiresLocal.map((d) => `${d.country} → ${d.language === '' ? 'language not stated' : d.language}`).join(' · '),
    })
  }

  if (determined.length === 0 && undetermined.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Authoritative language',
      title: 'Neither list came back with anything',
      body: 'No determination is on file and no open jurisdiction was reported either — which is not the same as counsel having answered everything. With no jurisdictions open there is nothing to determine yet; with jurisdictions open, this read did not see them.',
      ref: 'copy_authority · both lists empty',
    })
  }

  if (unreadable > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Partial read',
      title: `${String(unreadable)} row(s) could not be read`,
      body: 'Counted rather than dropped. A country missing from this screen is one nobody knows the standing of.',
      ref: 'a row with no country names no jurisdiction and is refused',
    })
  }

  blocks.push({
    t: 'note',
    tone: 'n',
    quiet: true,
      kicker: 'Recorded, not yet enforced',
    title: 'No customer document consults these determinations yet',
    body: 'They are recorded, versioned and readable here, and wiring them onto the disclosure renderer is a separate change. This screen states what counsel determined — not what a customer currently receives, which would be an enforcement claim this console cannot support.',
    ref: 'the register above is what counsel signed off',
  })

  const missing: string[] = []
  if (determinedCount === null) missing.push('how many determinations are on file')
  if (undeterminedCount === null) missing.push('how many open jurisdictions carry the default')

  return {
    blocks,
    provenance:
      missing.length === 0
        ? { kind: 'live', source: SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt) }
        : { kind: 'partial', source: SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt), missing },
    rowKeys: [...determined, ...undetermined].map((d) => d.country),
  }
}

export const loadCopyAuthority: ScreenLoader = async (api) => {
  let payload: unknown
  const who = callerBeside(api)
  try {
    payload = await api.get<unknown>(COPY_AUTHORITY_PATH)
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        blocks: [{
          t: 'note',
          tone: 'r',
          kicker: 'Authoritative language',
          title: 'The determinations could not be read',
          body: `The endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}. Which country is governed by which language, and which nobody has asked counsel about, are both unknown from this read — and an unread screen here is not an empty worklist.`,
          ref: error.decisionRef === null ? 'Nothing was read.' : `decision_ref ${error.decisionRef}`,
        }],
        provenance: { kind: 'unread', source: SOURCE, reason: `The endpoint answered ${String(error.status)}.` },
      }
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return {
      blocks: [{
        t: 'note',
        tone: 'r',
        kicker: 'Authoritative language',
        title: 'The determinations could not be read',
        body: 'The backend could not be reached. An unread screen here is not an empty worklist.',
        ref: 'Nothing was read.',
      }],
      provenance: { kind: 'unread', source: SOURCE, reason: 'The backend could not be reached.' },
    }
  }
  if (!isObject(payload)) {
    return {
      blocks: [{
        t: 'note',
        tone: 'r',
        kicker: 'Authoritative language',
        title: 'The determinations could not be read',
        body: 'The endpoint returned a shape this console does not know.',
        ref: 'Nothing was read.',
      }],
      provenance: { kind: 'unread', source: SOURCE, reason: 'The endpoint returned a shape this console does not know.' },
    }
  }
  const screen = composeAuthority(payload, new Date())
  return { ...screen, blocks: [...screen.blocks, authorityForm(api, await who)] }
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/**
 * Recording which language governs a country.
 *
 * # This is a determination, not a preference
 *
 * It decides which language's text is the one that binds when a translation and
 * the source disagree — and in several markets that is a question somebody had
 * to ask a lawyer. So the control asks for the counsel reference: a
 * determination with nobody's advice behind it is a guess with legal weight,
 * and the field is where the person who made it says who told them.
 *
 * It is not marked required, because the endpoint does not require it and a
 * console that refused where the server would accept would be inventing policy.
 * The note says what it is for, which is the honest way to ask for something
 * that matters and cannot be compelled here.
 *
 * # `requires_local_language` is a separate fact
 *
 * Some markets require the local language be OFFERED regardless of which one
 * governs. The two are independent — a country can be governed by English and
 * still owe its customers Dutch — so they are two fields rather than one
 * conflated switch.
 */
function authorityForm(api: ApiClient, caller: Caller): FormBlock {
  const blocked = writeRefusal(
    caller, 'copy.write', 'Ops', 'Recording an authoritative-language determination',
  )
  return {
    t: 'form',
    title: 'Record a determination',
    lead: 'Which language binds when a translation and the source disagree, for one country. This is a legal determination rather than a display preference.',
    fields: [
      {
        name: 'country', label: 'Country', kind: 'text', required: true, mono: true,
        placeholder: 'DE', note: 'ISO-3166-1 alpha-2.',
      },
      {
        name: 'authoritative_language', label: 'Authoritative language', kind: 'text',
        required: true, mono: true, placeholder: 'de',
        note: 'The version that binds if a translation and the source disagree.',
      },
      {
        name: 'requires_local_language', label: 'The local language must be offered',
        kind: 'checkbox', required: false,
        note: 'Independent of the above — a country can be governed by one language and still owe its customers another.',
      },
      {
        name: 'counsel_reference', label: 'Counsel reference', kind: 'text', required: false,
        mono: true,
        note: 'Who advised this. A determination with no advice behind it is a guess carrying legal weight; this is where the person who made it says who told them.',
      },
    ],
    reasons: [
      ['counsel_advice', 'On advice of counsel', ''],
      ['regulatory_requirement', 'Required by a regulator in this market', ''],
      ['market_launch', 'Opening this market', ''],
      ['correction', 'The recorded determination was wrong', ''],
    ],
    reasonNote: 'Recorded against the country.',
    auditLine: 'admin.copy.authority.set · actor · copy_authority:<country> · reason',
    cta: 'Record determination',
    ctaNote: 'Decides which text binds in a dispute.',
    blocked,
    ...(blocked === '' ? {
      submit: (v) => submitWrite(
        () => api.post<unknown>(`${COPY_AUTHORITY_PATH}/${String(v['country'])}`, {
          authoritative_language: v['authoritative_language'],
          requires_local_language: v['requires_local_language'] === 'true',
          counsel_reference: v['counsel_reference'] ?? '',
          reason: v['reason'],
        }),
        'The determination was recorded',
        () => [
          ['country', String(v['country']), ''],
          ['authoritative', String(v['authoritative_language']), 'binds in a dispute'],
        ],
      ),
    } : {}),
  }
}
